/**
 * A module that is routed but not built.
 *
 * It names the spec that governs it and says plainly that nothing is implemented, rather
 * than showing a plausible empty table. A screen that looks finished and holds nothing is
 * worse than one that admits it is not there yet — and only fictional data appears in this
 * build in any case.
 */

import { useLocation } from "react-router-dom";

import { moduleForPath } from "../app/modules.js";

/**
 * The spec that governs each module this page can still be reached for.
 *
 * Only the unbuilt modules belong here: a built module routes to its own screen and can never
 * render this one, so an entry for it is a line that could only ever be wrong. **The table is
 * now empty**, because every module in the strip is built — module 02's entry came out with
 * run 34, last of the eight, exactly as module 03's came out when `/fixtures` was built.
 *
 * The component itself stays, and is not dead: `App.tsx` routes the catch-all `*` to it, so an
 * unknown path lands on a page that says what it is rather than on a blank shell.
 */
const SPEC_FOR: Readonly<Record<string, string>> = {};

export function StubPage(): React.JSX.Element {
  const { pathname } = useLocation();
  const entry = moduleForPath(pathname);
  const spec = SPEC_FOR[entry.idx];

  return (
    <section className="panel panel--d1" style={{ marginTop: 26 }}>
      <div className="panel__head">
        <h3 className="panel__title">{entry.label}</h3>
        <span className="panel__note">Not Built Yet</span>
        <span className="spacer" />
        <span className="tbschip">
          <span aria-hidden="true">◆</span>Not Built Yet
        </span>
      </div>
      <div className="emptystate">
        <b>Module {entry.idx} is specified but not implemented.</b>
        {spec === undefined ? "No spec is recorded for this module." : `Specified in ${spec}.`}
      </div>
      <div className="panel__foot">
        Every module in the strip is built, so this page is now reached only by a path that
        belongs to none of them. Use the module strip above to get back to a screen that exists.
      </div>
    </section>
  );
}
